import { useMemo } from "react";
import type { FanChart as FanData, YearPoint } from "@retirement-planner/contract";
import { useLanguage } from "../i18n/LanguageProvider";
import { formatMoney } from "../lib/format";

const W = 480;
const H = 200;
const PAD = 8;

function pathFor(values: number[]): string {
  if (values.length === 0) return "";
  const max = Math.max(...values, 1);
  const stepX = (W - PAD * 2) / Math.max(values.length - 1, 1);
  return values
    .map((v, i) => {
      const x = PAD + i * stepX;
      const y = H - PAD - (v / max) * (H - PAD * 2);
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
}

/** Screen-reader path to the underlying numbers (WCAG 1.3.1). */
function ChartDataTable({ series, label }: { series: YearPoint[]; label: string }) {
  const { t, locale } = useLanguage();
  return (
    <details className="chart-disclosure">
      <summary>{t("viewDataTable")}</summary>
      <table className="chart-table">
        <caption className="visually-hidden">{label}</caption>
        <thead>
          <tr>
            <th scope="col">Age</th>
            <th scope="col">Real</th>
          </tr>
        </thead>
        <tbody>
          {series.map((p) => (
            <tr key={p.age}>
              <td>{p.age}</td>
              <td>{formatMoney(p.real.amountMinorUnits, p.real.currency, locale)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  );
}

function ChartTextSummary({ summary }: { summary: string }) {
  const { t } = useLanguage();
  return (
    <details className="chart-disclosure">
      <summary>{t("textSummary")}</summary>
      <p>{summary}</p>
    </details>
  );
}

/** Real (inflation-adjusted) nest-egg line. Lightweight memoized SVG. */
export function ProjectionChart({ series, label }: { series: YearPoint[]; label: string }) {
  const { locale } = useLanguage();
  const d = useMemo(() => pathFor(series.map((p) => p.real.amountMinorUnits)), [series]);
  const first = series[0];
  const last = series[series.length - 1];
  const summary =
    first && last
      ? `${label}: ${formatMoney(first.real.amountMinorUnits, first.real.currency, locale)} at age ${first.age} → ${formatMoney(last.real.amountMinorUnits, last.real.currency, locale)} real at age ${last.age}.`
      : label;
  return (
    <figure style={{ margin: 0 }}>
      <svg className="chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={summary}>
        <path d={d} fill="none" stroke="var(--color-accent)" strokeWidth="2" />
      </svg>
      <figcaption className="visually-hidden">{summary}</figcaption>
      <ChartTextSummary summary={summary} />
      <ChartDataTable series={series} label={label} />
    </figure>
  );
}

/** p10/p50/p90 fan. Bands distinguished by pattern/label, not color alone. */
export function FanChart({ fan, label }: { fan: FanData; label: string }) {
  const { locale } = useLanguage();
  const p10 = useMemo(() => pathFor(fan.p10.map((p) => p.real.amountMinorUnits)), [fan.p10]);
  const p50 = useMemo(() => pathFor(fan.p50.map((p) => p.real.amountMinorUnits)), [fan.p50]);
  const p90 = useMemo(() => pathFor(fan.p90.map((p) => p.real.amountMinorUnits)), [fan.p90]);
  const end = (pts: YearPoint[]) => pts[pts.length - 1];
  const summary =
    fan.p50.length > 0
      ? `Median (p50) ends at ${formatMoney(end(fan.p50).real.amountMinorUnits, end(fan.p50).real.currency, locale)}; pessimistic (p10) ${formatMoney(end(fan.p10).real.amountMinorUnits, end(fan.p10).real.currency, locale)}; optimistic (p90) ${formatMoney(end(fan.p90).real.amountMinorUnits, end(fan.p90).real.currency, locale)}, all real.`
      : label;
  return (
    <figure style={{ margin: 0 }}>
      <svg className="chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={summary}>
        <path d={p90} fill="none" stroke="var(--color-text-secondary)" strokeWidth="1.5" strokeDasharray="2 3" />
        <path d={p50} fill="none" stroke="var(--color-accent)" strokeWidth="2" />
        <path d={p10} fill="none" stroke="var(--color-text-secondary)" strokeWidth="1.5" strokeDasharray="6 3" />
      </svg>
      <figcaption className="visually-hidden">{summary}</figcaption>
      <ChartTextSummary summary={summary} />
      <ChartDataTable series={fan.p50} label={`${label} (p50)`} />
    </figure>
  );
}
